import React, { useEffect, useState } from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';
import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Ticker}
 */
const Ticker = ({
    tickerCtaCssClass = '',
    tickerCtaLabel = '',
    tickerCtaLink = null,
    tickerItems = null
}) => {
    const items = tickerItems || [];

    // state
    const [ duration, setDuration ] = useState(220);
    const [ textItems, setTextItems ] = useState(items);

    // hooks
    useEffect(() => {
        const handleResize = () => {
            let _items = JSON.parse(JSON.stringify(items));

            // item width fixed check css .ticker__item width
            const itemWidth = 300;

            // get how many items required to fill the current window size
            const itemsRequired = Math.ceil(window.innerWidth / itemWidth);

            // if not enough items, fill array sequentially until screen is filled
            if (_items.length > 0 && _items.length < itemsRequired) {
                while (_items.length < itemsRequired) {
                    _items = _items.concat(JSON.parse(JSON.stringify(items)));
                }

                _items = _items.slice(0, itemsRequired);

                setTextItems(_items);
            } else {
                setTextItems(_items);
            }

            // try to maintain consistent looking speed across screen when resizing
            // time in seconds / width of screen = pixels per second
            // 220s / 2560px = 0.0859375px/s

            setDuration(window.innerWidth * 0.0859375);
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // exit if no ticker items
    if (textItems.length < 1) {
        return '';
    }

    const isTickerCtaLinkLocal = isLocalURL(formatLinkBlockToUrl(tickerCtaLink));

    // render
    return (
        <div
            className='ticker relative w-full flex overflow-hidden bg-surface-strong h-60'
            role='marquee'>
            {/* Book Button */}
            <a
                className={`absolute top-0 left-0 h-full flex items-center bg-surface-emphasis px-16 py-19 type-style-sans-body-med z-10${tickerCtaCssClass ? ' ' + tickerCtaCssClass: ''}`}
                href={formatLinkBlockToUrl(tickerCtaLink)}
                rel={isTickerCtaLinkLocal ? undefined : 'noreferrer'}
                target={isTickerCtaLinkLocal ? undefined : '_blank' }>
                {tickerCtaLabel}
            </a>

            {/* Output ticker twice to fake marquee effect */}
            {[0, 1].map((_block, bdx) => {
                return (
                    <ul
                        className='ticker__items w-fit h-full flex flex-shrink-0 flex-grow-0'
                        key={bdx}
                        style={{ animationDuration: duration + 's' }}>
                        {textItems.map((item, idx) => {
                            const isItemCtaLinkLocal = isLocalURL(formatLinkBlockToUrl(item.ctaLink));

                            return (
                                <li
                                    className='ticker__item flex-shrink-0 flex-grow-0 flex items-center justify-start whitespace-nowrap py-8'
                                    key={idx}>
                                    <div className='w-full'>
                                        {item?.text && (
                                            <p className='type-style-sans-body px-15 md:px-30 w-full overflow-hidden overflow-ellipsis'>
                                                {item.text}
                                            </p>
                                        )}

                                        {item?.ctaLabel && (
                                            <p className='type-style-sans-body-med px-15 md:px-30 w-full overflow-hidden overflow-ellipsis'>
                                                <a
                                                    className={`underline underline-offset-2${item.ctaCssClass ? ' ' + item.ctaCssClass: ''}`}
                                                    href={formatLinkBlockToUrl(item.ctaLink)}
                                                    rel={isItemCtaLinkLocal ? undefined : 'noreferrer'}
                                                    target={isItemCtaLinkLocal ? undefined : '_blank' }>
                                                    {item.ctaLabel}
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                );
            })}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Ticker;
